import {Component, Element, Event, EventEmitter, getAssetPath, h, Host, Prop, State, Watch} from '@stencil/core';
import {ImageLoadingStatus, RotationImage, THEME} from '../../globals/typings';
import {CarouselImage} from '../marketing-cc/ConfigData';
import { TranslationService } from '../../services/translation-service';

@Component({
  tag: 'mcc-rotation-view',
  styleUrl: 'rotation-view.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class RotationView {
  @Event({
    eventName: 'mccRotationViewError',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) rotationViewError: EventEmitter<void>;

  @Prop() rotationImages: CarouselImage[];
  @Prop() theme: THEME;

  @State() private imageIndex = 0;
  @State() private computedRotationImages: RotationImage[];
  @State() private allImagesFailed = false;
  @State() showCircle: boolean = true;

  @Element() private host: HTMLElement;

  private readonly translationService = TranslationService;
  private eventActive = false;
  private swipeCoord?: [number, number];
  private lastEvent: number;
  private assetTheme: THEME;

  private htmlImageContainer: HTMLDivElement;

  componentWillLoad() {
    this.watchRotationImagesHandler(this.rotationImages);
    this.assetTheme = this.theme === THEME.LNF22 ? THEME.VW22 : this.theme;
  }

  componentDidRender() {
    this.htmlImageContainer = this.host.shadowRoot.querySelector('.images-container');

    this.htmlImageContainer.addEventListener('dragstart', (e) => e.preventDefault());

    // touch events
    this.htmlImageContainer.addEventListener('touchstart', (e) => this.onSwipe(e, 'start'), {passive: true});
    this.htmlImageContainer.addEventListener('touchend', () => this.onSwipe(null, 'end'));
    this.htmlImageContainer.addEventListener('touchmove', (e) => this.onSwipe(e, 'move'), {passive: true});

    // mouse events
    this.htmlImageContainer.addEventListener('mousedown', (e) => this.onDrag(e, 'start'));
    this.htmlImageContainer.addEventListener('mouseup', () => this.onDrag(null, 'end'));
    this.htmlImageContainer.addEventListener('mouseleave', () => this.onDrag(null, 'end'));
    this.htmlImageContainer.addEventListener('mousemove', (e) => this.onDrag(e, 'move'));
  }

  onSwipe(event: TouchEvent, when: string) {
    if (!event) {
      this.eventActive = false;
      return;
    }

    this.checkNewCoordinates(event, [event.changedTouches[0].pageX, event.changedTouches[0].pageY], when);
  }

  onDrag(event: MouseEvent, when: string) {
    if (!event) {
      this.eventActive = false;
      return;
    }

    event.preventDefault();
    this.checkNewCoordinates(event, [event.clientX, event.clientY], when);
  }

  private checkNewCoordinates(event: UIEvent, coord: [number, number], when: string) {
    if (when === 'start') {
      this.eventActive = true;

      this.swipeCoord = coord;
    } else if (when === 'move') {
      if (this.eventActive) {
        this.showCircle = false;
        // Set timer between 30 or 100ms based on number of images
        const timeToWait = this.rotationImages.length > 15 ? 30 : 100;
        // Only Trigger events every 30-100ms discard others
        if ((event?.timeStamp - this.lastEvent) < timeToWait) {
          return;
        }
        this.lastEvent = event?.timeStamp;

        const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
        this.swipeCoord = coord;

        // Check for horizontal change
        if (Math.abs(direction[0]) > Math.abs(direction[1] * 3)) {
          this.changeSlide(direction[0] < 0 ? 'PREV' : 'NEXT');
        }
      }
    }
  }

  private changeSlide(direction: 'NEXT' | 'PREV', ignoreError = false) {
    this.showCircle = false;
    if (direction === 'PREV') {
      this.imageIndex = this.imageIndex - 1;
    } else {
      this.imageIndex = this.imageIndex + 1;
    }

    if (this.imageIndex < 0) {
      this.imageIndex = this.rotationImages.length - 1;
    } else if (this.computedRotationImages.length <= this.imageIndex) {
      this.imageIndex = 0;
    }

    if (!ignoreError && this.computedRotationImages[this.imageIndex].loadingStatus === ImageLoadingStatus.error) {
      this.changeSlide(direction);
    }
  }

  onImageLoad(imageInfo: RotationImage, _index: number) {
    imageInfo.loadingStatus = ImageLoadingStatus.success;
    const index = this.computedRotationImages.findIndex(img => img.url === imageInfo.url);
    if (index !== -1) {
      this.computedRotationImages[index].loadingStatus = ImageLoadingStatus.success;
    }
  }

  onImageError(imageInfo: RotationImage, _index: number) {
    imageInfo.loadingStatus = ImageLoadingStatus.error;
    const index = this.computedRotationImages.findIndex(img => img.url === imageInfo.url);
    if (index !== -1) {
      this.computedRotationImages[index].loadingStatus = ImageLoadingStatus.error;
    }
    this.allImagesFailed = this.computedRotationImages.every(rotationImage => rotationImage.loadingStatus === ImageLoadingStatus.error);
  }

  @Watch('imageIndex')
  checkIndex(index: number) {
    return this.imageIndex === index ? ' active' : '';
  }

  @Watch('rotationImages')
  watchRotationImagesHandler(newRotationImageList: CarouselImage[]) {
    this.computeRotationImages(newRotationImageList);
  }

  computeRotationImages(rotationImageList: CarouselImage[]) {
    this.computedRotationImages = new Array<RotationImage>();

    rotationImageList.forEach(rotationImage => {
      const imageInfo = new RotationImage(rotationImage.url);
      imageInfo.loadingStatus = ImageLoadingStatus.loading;
      this.computedRotationImages.push(imageInfo);
    });
  }

  handleRotationViewKeyDown(event: KeyboardEvent) {
    if (['ArrowRight', 'ArrowDown'].includes(event.code)) {
      event.preventDefault();
      this.changeSlide('NEXT');
    }
    if (['ArrowLeft', 'ArrowUp'].includes(event.code)) {
      event.preventDefault();
      this.changeSlide('PREV');
    }
  }

  render() {
    return (
      <Host>
        <div id="nwi-root" class={this.theme}>
          <div class="rotation-wrapper"
               tabIndex={0}
               role="listbox"
               aria-label={this.translationService.get('mcc.image-selector.text')}
               onKeyDown={e => this.handleRotationViewKeyDown(e)}>
            <div class="images-container">
              {!this.allImagesFailed && this.computedRotationImages.map((image, i) =>
                <div class={'image-wrapper' + this.checkIndex(i)} key={"images"}>
                  <fading-image src={image.url}
                                role="option"
                                alt="car image"
                                onLoad={() => this.onImageLoad(image, i)}
                                onError={() => this.onImageError(image, i)}/>
                </div>
              )}
            </div>
            {!this.allImagesFailed &&
              <div class={'circle-container' + (this.showCircle ? '' : ' hidden')} aria-hidden="true">
                <img class="prev-icon" alt={this.translationService.get('mcc.prev-icon.text')} src={getAssetPath(`./assets/${this.assetTheme}/button-backward.svg`)} style={{ animation: '1.3s ease-in-out infinite drag-btn-anim' }}/>
                <img class="next-icon" alt={this.translationService.get('mcc.next-icon.text')} src={getAssetPath(`./assets/${this.assetTheme}/button-forward.svg`)} style={{ animation: '1.3s ease-in-out infinite drag-btn-anim-inverted' }}/>
              </div>
            }
            {this.allImagesFailed &&
              <div class="fallback">
                <img src={getAssetPath(`./assets/rotation-image-not-found.svg`)}
                     role="option"
                     alt="car image not found"
                     class="fallback-image"
                     key={'fallback-image'}/>
              </div>
            }
          </div>
        </div>
      </Host>
    )
  }
}
